/**
 * Circuit Breaker Pattern
 * Protège l'application contre les échecs répétés
 * 
 * États :
 * - CLOSED : Tout fonctionne normalement
 * - OPEN : Trop d'échecs, désactivation temporaire
 * - HALF_OPEN : Test de récupération
 */

import { LIMITS } from '@/config/features'
import { logger } from '@/utils/productionLogger'

type CircuitState = 'CLOSED' | 'OPEN' | 'HALF_OPEN'

export class CircuitBreaker {
  private failures = 0
  private lastFailure = 0
  private state: CircuitState = 'CLOSED'
  private readonly name: string
  
  constructor(name: string) {
    this.name = name
  }
  
  /**
   * Exécute une fonction avec protection circuit breaker
   * Si trop d'échecs, utilise le fallback
   */
  async execute<T>(
    fn: () => Promise<T>, 
    fallback: () => T
  ): Promise<T> {
    // Si circuit ouvert, vérifier si on peut réessayer
    if (this.state === 'OPEN') {
      const timeSinceLastFailure = Date.now() - this.lastFailure
      
      if (timeSinceLastFailure > LIMITS.CIRCUIT_BREAKER_TIMEOUT) {
        this.state = 'HALF_OPEN'
        logger.info(`[CircuitBreaker:${this.name}] Attempting recovery (HALF_OPEN)`)
      } else {
        logger.warn(`[CircuitBreaker:${this.name}] Circuit OPEN, using fallback`)
        return fallback()
      }
    }
    
    try {
      const result = await fn()
      this.onSuccess()
      return result
    } catch (error) {
      this.onFailure()
      logger.error(`[CircuitBreaker:${this.name}] Execution failed`, error as Error)
      return fallback()
    }
  }
  
  /**
   * Appelé en cas de succès
   */
  private onSuccess() {
    this.failures = 0
    
    if (this.state === 'HALF_OPEN') {
      logger.info(`[CircuitBreaker:${this.name}] Recovery successful, circuit CLOSED`)
    }
    
    this.state = 'CLOSED'
  }
  
  /**
   * Appelé en cas d'échec
   */
  private onFailure() {
    this.failures++
    this.lastFailure = Date.now()
    
    if (this.failures >= LIMITS.CIRCUIT_BREAKER_THRESHOLD) {
      this.state = 'OPEN'
      logger.error(
        `[CircuitBreaker:${this.name}] Circuit OPENED after ${this.failures} failures`
      )
    }
  }
  
  /**
   * Réinitialise le circuit breaker
   */
  reset() {
    this.failures = 0
    this.lastFailure = 0
    this.state = 'CLOSED'
    logger.info(`[CircuitBreaker:${this.name}] Circuit reset`)
  }
  
  /**
   * Retourne l'état actuel
   */
  getState(): CircuitState {
    return this.state
  }
  
  /**
   * Retourne les métriques
   */
  getMetrics() {
    return {
      state: this.state,
      failures: this.failures,
      lastFailure: this.lastFailure,
    }
  }
}

// Circuit breaker global pour Realtime
export const realtimeCircuitBreaker = new CircuitBreaker('Realtime')
